import {Routes} from "@angular/router";
import {provideState} from "@ngrx/store";
import {AutoLoginPartialRoutesGuard} from "angular-auth-oidc-client";
import {adminGuard} from "./guards/auth/admin.guard";
import {userGuard} from "./guards/auth/user.guard";
import {MtcRouteInterface} from "./interfaces";
import {assetsFeature, assetTypesFeature} from "./store/reducers";

export const routes: (Routes[number] | MtcRouteInterface)[] = [
  {
    path: "",
    title: "Unauthorized",
    pathMatch: "full",
    loadComponent: () => import("./components/unauthorized/unauthorized.component").then(mod => mod.UnauthorizedComponent)
  },
  {
    path: "asset-management/asset-type/assets/:id",
    canActivate: [AutoLoginPartialRoutesGuard, userGuard],
    providers: [provideState(assetTypesFeature)],
    loadComponent: () =>
      import("./components/asset-management/asset-type-assets/asset-type-assets.component").then(
        mod => mod.AssetTypeAssetsComponent
      )
  },
  {
    path: "asset-management/asset-type/:id",
    canActivate: [AutoLoginPartialRoutesGuard, userGuard],
    providers: [provideState(assetTypesFeature)],
    loadComponent: () =>
      import("./components/asset-management/asset-type-detail/asset-type-detail.component").then(
        mod => mod.AssetTypeDetailComponent
      )
  },
  {
    path: "asset-management/asset/:id",
    pathMatch: "full",
    canActivate: [AutoLoginPartialRoutesGuard, userGuard],
    providers: [provideState(assetsFeature)],
    loadComponent: () =>
      import("./components/asset-management/asset-detail/asset-detail.component").then(mod => mod.AssetDetailComponent)
  },
  {
    path: "asset-management",
    title: "Asset Management",
    canActivate: [AutoLoginPartialRoutesGuard, userGuard],
    canActivateChild: [AutoLoginPartialRoutesGuard, userGuard],
    pathMatch: "prefix",
    providers: [provideState(assetTypesFeature)],
    loadComponent: () =>
      import("./components/asset-management/asset-management.component").then(mod => mod.AssetManagementComponent),
    children: [
      {
        path: "asset-type/assets/:id",
        loadComponent: () =>
          import("./components/asset-management/asset-type-assets/asset-type-assets.component").then(
            mod => mod.AssetTypeAssetsComponent
          )
      },
      {
        path: "asset-type/:id",
        loadComponent: () =>
          import("./components/asset-management/asset-type-detail/asset-type-detail.component").then(
            mod => mod.AssetTypeDetailComponent
          )
      },
      {
        path: "asset-type",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/asset-management/asset-type-list/asset-type-list.component").then(mod => mod.AssetTypeListComponent)
      },
      {
        path: "asset",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/asset-management/asset-list/asset-list.component").then(mod => mod.AssetListComponent)
      },
      {
        path: "technology",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/asset-management/technology-list/technology-list.component").then(mod => mod.TechnologyListComponent)
      },
      {
        path: "",
        pathMatch: "full",
        redirectTo: "asset"
      }
    ]
  },
  {
    path: "settings",
    title: "Settings",
    pathMatch: "prefix",
    canActivate: [AutoLoginPartialRoutesGuard, adminGuard],
    loadComponent: () => import("./components/settings/settings.component").then(mod => mod.SettingsComponent),
    children: [
      {
        path: "trumpf-organisation",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/settings/organisations/organisations.component").then(mod => mod.OrganisationsComponent)
      },
      {
        path: "customer-mapping",
        pathMatch: "full",
        loadComponent: () => import("./components/settings/mapping/mapping.component").then(mod => mod.MappingComponent)
      },
      {
        path: "access-control",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/settings/access-control/access-control.component").then(mod => mod.AccessControlComponent)
      },
      {
        path: "",
        pathMatch: "full",
        redirectTo: "trumpf-organisation"
      }
    ]
  },
  {
    path: "feature-toggles",
    title: "Feature Toggles",
    pathMatch: "prefix",
    canActivate: [AutoLoginPartialRoutesGuard, userGuard],
    canActivateChild: [AutoLoginPartialRoutesGuard, userGuard],
    loadComponent: () => import("./components/feature-toggles/feature-toggles.component").then(mod => mod.FeatureTogglesComponent),
    children: [
      {
        path: ":environment",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/feature-toggles/feature-toggle-list/feature-toggle-list.component").then(
            mod => mod.FeatureToggleListComponent
          )
      },
      {
        path: "",
        pathMatch: "full",
        redirectTo: "integration"
      }
    ]
  },
  {
    path: "feature-toggles/:environment/:id",
    title: "Feature Toggle Details",
    pathMatch: "prefix",
    canActivate: [AutoLoginPartialRoutesGuard, userGuard],
    canActivateChild: [AutoLoginPartialRoutesGuard, userGuard],
    loadComponent: () =>
      import("./components/feature-toggles/feature-toggle-details-wrapper/feature-toggle-details-wrapper.component").then(
        mod => mod.FeatureToggleDetailsWrapperComponent
      ),
    children: [
      {
        path: "details",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/feature-toggles/feature-toggle-details/feature-toggle-details.component").then(
            mod => mod.FeatureToggleDetailsComponent
          )
      },
      {
        path: "changelog",
        pathMatch: "full",
        loadComponent: () =>
          import("./components/feature-toggles/feature-toggle-changelog/feature-toggle-changelog.component").then(
            mod => mod.FeatureToggleChangelogComponent
          )
      },
      {
        path: "",
        pathMatch: "full",
        redirectTo: "details"
      }
    ]
  },
  {
    path: "logout",
    title: "Logout",
    pathMatch: "full",
    loadComponent: () => import("./components/logout/logout.component").then(mod => mod.LogoutComponent)
  },
  {
    path: "unauthorized",
    redirectTo: ""
  }
];
